import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

type Commitment = { icon: string; label: string; note: string };

export default function AboutAttorneySection() {
  const { t } = useTranslation();

  const commitments = t("aboutAtty.commitments", { returnObjects: true }) as Commitment[];

  return (
    <section className="py-24 bg-[#EDEEF3]">
      <div className="max-w-7xl mx-auto px-6">

        {/* Section header */}
        <div className="mb-16">
          <span
            className="text-[#8A7A6A] block leading-none mb-2"
            style={{ fontFamily: "'Great Vibes', cursive", fontSize: "38px" }}
          >
            {t("aboutAtty.sectionCalligraphy")}
          </span>
          <h2
            className="text-[#1E202A] leading-tight"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(28px, 3.5vw, 44px)", fontWeight: 300 }}
          >
            {t("aboutAtty.storyTitle1")}
            <br />
            <em>{t("aboutAtty.storyTitle2")}</em>
          </h2>
        </div>

        {/* 2-column: story left, attorney right */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 mb-20">

          {/* Left — story narrative */}
          <div>
            <p className="text-[#74758A] text-sm leading-[2] mb-5">{t("aboutAtty.storyP1")}</p>
            <p className="text-[#74758A] text-sm leading-[2] mb-5">{t("aboutAtty.storyP2")}</p>
            <p className="text-[#74758A] text-sm leading-[2] mb-10">{t("aboutAtty.storyP3")}</p>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 text-sm font-medium text-[#1E202A] border-b border-[#1E202A]/25 pb-0.5 hover:border-[#8A7A6A] hover:text-[#8A7A6A] transition-all duration-300 cursor-pointer"
              style={{ letterSpacing: "0.02em" }}
            >
              {t("aboutAtty.learnMore")}
              <i className="ri-arrow-right-line text-xs" />
            </Link>
          </div>

          {/* Right — attorney profile */}
          <div>
            <span
              className="text-[#8A7A6A]/60 block leading-none mb-6"
              style={{ fontFamily: "'Great Vibes', cursive", fontSize: "30px" }}
            >
              {t("aboutAtty.attyCalligraphy")}
            </span>

            <div className="mb-8">
              <div>
                <h3
                  className="text-[#1E202A] leading-none mb-1"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(22px, 2.5vw, 30px)", fontWeight: 400 }}
                >
                  {t("brand.attorneyName")}
                </h3>
                <p
                  className="tracking-[0.18em] uppercase mb-2"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "10px", fontWeight: 600, color: "#B8942A" }}
                >
                  {t("brand.attorneyTitle")}
                </p>
                <p className="text-[#A8AABB] text-[11px] tracking-widest">
                  {t("brand.yearsValue")} {t("brand.yearsLabel")} &nbsp;·&nbsp; {t("brand.languagesLabel")}: English · 한국어 · 中文
                </p>
              </div>
            </div>

            <blockquote className="pl-5 border-l-2 border-[#8A7A6A]/30 mb-8">
              <p
                className="text-[#1E202A]/75 italic leading-relaxed"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(15px, 1.6vw, 18px)", fontWeight: 300 }}
              >
                {t("aboutAtty.quote")}
              </p>
            </blockquote>

            <Link
              to="/about"
              className="inline-flex items-center gap-2 text-sm font-medium text-[#1E202A] border-b border-[#1E202A]/25 pb-0.5 hover:border-[#8A7A6A] hover:text-[#8A7A6A] transition-all duration-300 cursor-pointer"
              style={{ letterSpacing: "0.02em" }}
            >
              {t("aboutAtty.fullProfile")}
              <i className="ri-arrow-right-line text-xs" />
            </Link>
          </div>
        </div>

        {/* Bottom: commitments row */}
        <div className="w-full h-px bg-[#D2D4DE]/70 mb-12" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0">
          {Array.isArray(commitments) && commitments.map((c, i) => (
            <div
              key={i}
              className={`flex items-start gap-4 py-6 ${i > 0 ? "lg:pl-8 lg:border-l border-[#D2D4DE]" : ""} ${i > 0 && i < 2 ? "sm:pl-8 sm:border-l" : ""}`}
            >
              <div className="w-7 h-7 flex items-center justify-center shrink-0 mt-0.5">
                <i className={`${c.icon} text-[#8A7A6A] text-base`} />
              </div>
              <div>
                <p
                  className="text-[#1E202A] mb-1"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "17px", fontWeight: 500 }}
                >
                  {c.label}
                </p>
                <p className="text-[#74758A] text-xs leading-relaxed">{c.note}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
